import React, { useContext } from 'react'
import { Event, Price, Cart } from 'app/types/authorization'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import {
  Arena as ArenaType,
  ArenaSection,
  ArenaSectionSeatTypes,
} from 'app/types/arena'
import { StandardAPIRequest } from 'app/types/api'
import { clientGetIdToken } from 'app/utils/cognito'
import { CompanyProfile } from 'app/types/organization'
import { ChevronLeftIcon } from '@heroicons/react/20/solid'
import { AuthContext } from 'app/utils/context'
import OrganizerArena from '../components/old/OrganizerArena'
import { captureException } from '@sentry/nextjs'
import UnseatedArena from '../components/old/UnseatedArena'
import { ArenaSectionComponent } from './ArenaSectionComponent'
import Currency from 'app/types/currency'

// Types
export interface ArenaProps {
  event: Event
  arena: ArenaType | null
  sections: ArenaSection[]
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>
  setShowSuccessMessage: React.Dispatch<React.SetStateAction<string>>
  setShowFailureMessage: React.Dispatch<React.SetStateAction<string>>
  refetchCart: () => void
  ticketsInCart: string[] | undefined
  tickets: ArenaSectionSeatTypes[][]
  selectedSection: ArenaSection | undefined
  setSelectedSection: React.Dispatch<
    React.SetStateAction<ArenaSection | undefined>
  >
  fetchTickets: () => Promise<void>
  sectionLoading: boolean
  companyProfile: CompanyProfile | null
  currency: Currency
}

// Components
const BackButton: React.FC<{ onClick: () => void; text: string }> = ({
  onClick,
  text,
}) => (
  <div onClick={onClick} className="flex cursor-pointer flex-row items-center">
    <ChevronLeftIcon className="mr-0.5 h-6 w-6 text-gray-800" />
    <p className="text-gray-800">{text}</p>
  </div>
)

const PageTitle: React.FC<{ title: string }> = ({ title }) => (
  <h2 className="text-3xl font-semibold leading-tight text-gray-800">
    {title}
  </h2>
)

// Main component
export function Arena({
  event,
  arena,
  sections,
  setCurrentStep,
  setShowSuccessMessage,
  setShowFailureMessage,
  refetchCart,
  ticketsInCart,
  setSelectedSection,
  selectedSection,
  tickets,
  fetchTickets,
  sectionLoading,
  companyProfile,
  currency,
}: ArenaProps) {
  const { t } = useTranslation('common')
  const { state } = useContext(AuthContext)

  // Filter sections
  const clickableSections = sections
    .filter((section) => section.clickable)
    ?.map((section) => section.svg_id ?? '')
    .filter((section) => section.length > 0)

  const blockedSections = sections
    .filter((section) => section.blocked_section)
    ?.map((section) => section.svg_id ?? '')
    .filter((section) => section.length > 0)

  // Function to add unnumbered ticket to cart
  const addUnnumberedTicketToCart = async (
    event: string,
    section: string,
    price: Price,
    numberOfTickets: number = 1
  ) => {
    const idToken = await clientGetIdToken()
    try {
      let headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Cart-Token': state.cartToken,
      }
      if (idToken) {
        headers.Authorization = `Bearer ${idToken}`
      }
      let response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/checkout/${event}/cart/generic_ticket`,
        {
          method: 'PUT',
          body: JSON.stringify({
            event,
            section,
            price,
            number_of_tickets: numberOfTickets,
          }),
          headers,
        }
      )
      let res: StandardAPIRequest<Cart | null> = await response.json()
      if (res.data !== null) {
        setShowSuccessMessage(t('ticket-added-to-basket') ?? '')
        refetchCart()
      } else if (res.error) {
        setShowFailureMessage(res.error.message ?? '')
        throw new Error(res.error.message)
      } else {
        setShowFailureMessage(t('unable-to-add-ticket') ?? '')
      }
    } catch (err) {
      captureException(err)
      setShowFailureMessage(t('unable-to-add-ticket') ?? '')
    }
  }

  // Function to handle section press
  const pressedSection = (section_svg_id: string) => {
    let section = sections.find((section) => section.svg_id === section_svg_id)
    if (section && section.clickable) {
      if (section.seated_section) {
        setSelectedSection(section)
      } else if (section.price !== null) {
        addUnnumberedTicketToCart(
          event.sk,
          section.uuid,
          section?.price[0] ?? {
            value: 0,
            name: '',
            requires_verification: null,
          }
        )
      }
    }
  }

  return (
    <div className="max-w-screen w-screen overflow-scroll sm:w-full">
      {/* Mobile back button */}
      <div className="block sm:hidden">
        {selectedSection ? (
          <div className="mx-auto my-4 flex max-w-4xl">
            <BackButton
              onClick={() => setSelectedSection(undefined)}
              text={t('back-to-all-sections')}
            />
          </div>
        ) : (
          <div className="mx-auto my-4 flex max-w-4xl">
            <Link
              target="_self"
              href={`/stores/${event.organization}`}
              className="flex cursor-pointer flex-row items-center"
            >
              <BackButton onClick={() => {}} text={t('back-to-store')} />
            </Link>
          </div>
        )}
      </div>

      {/* Page title and desktop back button */}
      <div className="mx-10 my-8 flex items-center justify-center sm:justify-between">
        {selectedSection ? (
          <>
            <div className="-ml-2 hidden cursor-pointer flex-row items-center sm:flex">
              <BackButton
                onClick={() => setSelectedSection(undefined)}
                text={t('back-to-all-sections')}
              />
            </div>
            <PageTitle
              title={t('section-name', { name: selectedSection.name })}
            />
          </>
        ) : (
          <>
            <Link
              href={`/stores/${event.organization}`}
              className="-ml-2 hidden cursor-pointer flex-row items-center sm:flex"
            >
              <BackButton onClick={() => {}} text={t('back-to-all-matches')} />
            </Link>
            <PageTitle
              title={
                arena?.svg_name !== null && arena?.svg_name !== undefined
                  ? t('choose-section')
                  : t('choose-ticket')
              }
            />
          </>
        )}
        <div className="hidden h-1 w-1 opacity-0 sm:block" />
      </div>

      {/* Main content */}
      <div className="px-0 py-8">
        {selectedSection ? (
          <ArenaSectionComponent
            event={event}
            section={selectedSection}
            setCurrentStep={setCurrentStep}
            setShowSuccessMessage={setShowSuccessMessage}
            setShowFailureMessage={setShowFailureMessage}
            refetchCart={refetchCart}
            ticketsInCart={ticketsInCart}
            tickets={tickets}
            fetchTickets={fetchTickets}
            sectionLoading={sectionLoading}
          />
        ) : (
          <React.Fragment>
            {arena?.svg_name !== null && arena?.svg_name !== undefined ? (
              <div className="px-4 sm:px-10">
                <OrganizerArena
                  clickableSections={clickableSections}
                  blockedSections={blockedSections}
                  setActive={pressedSection}
                  url={arena?.svg_name}
                />
              </div>
            ) : (
              <div className="px-4 sm:px-10">
                <UnseatedArena
                  eventSk={event.sk}
                  sections={sections}
                  companyProfile={companyProfile}
                  addUnseatedTicket={addUnnumberedTicketToCart}
                  currency={currency}
                />
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  )
}

// Extracted text strings
// t('back-to-all-sections') - "Tillbaka till alla sektioner"
// t('back-to-store') - "Tillbaka till butiken"
// t('section-name', { name: selectedSection.name }) - "Sektion ${selectedSection.name}"
// t('back-to-all-matches') - "Tillbaka till alla matcher"
// t('choose-section') - "Välj sektion"
// t('choose-ticket') - "Välj biljett"
// t('unable-to-add-ticket') - "Unable to add ticket."
